import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n";
import { useBooking } from "@/BookingContext";

export default function Navbar() {
  const { lang, setLang, t, simg } = useLang();
  const { openBooking } = useBooking();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const plain =
    location.pathname.startsWith("/espace-proprietaire") || location.pathname.startsWith("/admin");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const dark = (scrolled || plain) && !open;
  const tone = dark ? "text-maquis-deep" : "text-casse";

  const links = [
    { to: "/#conciergerie", label: t.nav.concierge, testid: "nav-link-conciergerie" },
    { to: "/services", label: t.nav.services, testid: "nav-link-services" },
    { to: "/locations", label: t.nav.locations, testid: "nav-link-locations" },
    { to: "/prestataires", label: t.nav.providers, testid: "nav-link-prestataires" },
    { to: "/contact", label: t.nav.contact, testid: "nav-link-contact" },
  ];

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-[80] transition-[background-color,border-color] duration-300 ${
          dark ? "glass-nav" : "border-b border-transparent"
        } ${open ? "bg-maquis-deep" : ""}`}
      >
        <div className={`flex w-full items-center justify-between px-4 sm:px-8 py-3 ${open ? "text-casse" : tone}`}>
          <Link to="/" data-testid="nav-brand" className="shrink-0" aria-label="Présence Home Services">
            <img
              src={dark ? simg("logoBlack") : simg("logoWhite")}
              alt="Présence Home Services"
              className={`w-auto transition-all duration-300 ${dark ? "h-9 sm:h-11" : "h-12 sm:h-16"}`}
            />
          </Link>

          <div className="hidden lg:flex items-center gap-7">
            <nav className="flex items-center gap-7">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={l.testid}
                  className="text-xs uppercase tracking-[0.22em] opacity-80 hover:opacity-100 transition-opacity duration-200"
                >
                  {l.label}
                </Link>
              ))}
            </nav>
            <div className="flex items-center gap-2 text-[11px] tracking-[0.2em]" data-testid="lang-switcher">
              <button
                data-testid="lang-switch-fr"
                onClick={() => setLang("fr")}
                className={lang === "fr" ? "opacity-100 font-semibold" : "opacity-50 hover:opacity-80"}
              >
                FR
              </button>
              <span className="opacity-40">|</span>
              <button
                data-testid="lang-switch-en"
                onClick={() => setLang("en")}
                className={lang === "en" ? "opacity-100 font-semibold" : "opacity-50 hover:opacity-80"}
              >
                EN
              </button>
            </div>
            <button
              data-testid="nav-reserve-button"
              onClick={() => openBooking()}
              className={`btn-outline-elegant !px-5 !py-2 !text-[11px] ${
                dark
                  ? "text-maquis-deep hover:bg-maquis-deep hover:text-casse"
                  : "text-casse hover:bg-casse hover:text-maquis-deep"
              }`}
            >
              {t.nav.reserve}
            </button>
          </div>

          <button
            data-testid="mobile-menu-button"
            className="lg:hidden p-2 -mr-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={24} strokeWidth={1.25} /> : <Menu size={24} strokeWidth={1.25} />}
          </button>
        </div>
      </header>

      {open && (
        <div
          data-testid="mobile-menu"
          className="fixed inset-0 z-[70] bg-maquis-deep text-casse flex flex-col items-center justify-center gap-8 lg:hidden"
        >
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`mobile-${l.testid}`}
              className="font-serif text-3xl tracking-wide"
            >
              {l.label}
            </Link>
          ))}
          <button
            data-testid="mobile-nav-reserve-button"
            onClick={() => {
              setOpen(false);
              openBooking();
            }}
            className="btn-outline-elegant text-casse hover:bg-casse hover:text-maquis-deep mt-4"
          >
            {t.nav.reserve}
          </button>
          <Link to="/espace-proprietaire" data-testid="mobile-nav-owner-link" className="text-xs uppercase tracking-[0.22em] opacity-70 mt-2">
            {t.nav.owner}
          </Link>
          <div className="flex items-center gap-3 text-sm tracking-[0.2em] mt-4">
            <button data-testid="mobile-lang-fr" onClick={() => setLang("fr")} className={lang === "fr" ? "font-semibold" : "opacity-50"}>FR</button>
            <span className="opacity-40">|</span>
            <button data-testid="mobile-lang-en" onClick={() => setLang("en")} className={lang === "en" ? "font-semibold" : "opacity-50"}>EN</button>
          </div>
        </div>
      )}
    </>
  );
}
